'use client'

import * as React from 'react'
import { Slider as SliderPrimitive } from '@base-ui/react/slider'

import { cn } from '@/lib/utils'

type SliderValue = number[]

type SliderProps = Omit<
  SliderPrimitive.Root.Props,
  'value' | 'defaultValue' | 'onValueChange' | 'onValueCommitted'
> & {
  className?: string
  value?: SliderValue
  defaultValue?: SliderValue
  onValueChange?: (value: SliderValue) => void
  onValueCommit?: (value: SliderValue) => void
  getAriaLabel?: (index: number) => string
  'aria-label'?: string
}

function toArrayValue(value: number | number[]): SliderValue {
  return Array.isArray(value) ? value : [value]
}

function Slider({
  className,
  value,
  defaultValue,
  onValueChange,
  onValueCommit,
  getAriaLabel,
  'aria-label': ariaLabel,
  ...props
}: SliderProps) {
  const thumbValues = value ?? defaultValue ?? [props.min ?? 0]

  return (
    <SliderPrimitive.Root
      data-slot="slider"
      value={value}
      defaultValue={defaultValue}
      onValueChange={(nextValue) => onValueChange?.(toArrayValue(nextValue))}
      onValueCommitted={(nextValue) => onValueCommit?.(toArrayValue(nextValue))}
      {...props}
    >
      <SliderPrimitive.Control
        className={cn(
          'relative flex w-full touch-none select-none items-center',
          className,
        )}
      >
        <SliderPrimitive.Track className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-primary/20">
          <SliderPrimitive.Indicator className="absolute h-full bg-primary" />
          {thumbValues.map((_, index) => (
            <SliderPrimitive.Thumb
              key={index}
              index={thumbValues.length > 1 ? index : undefined}
              aria-label={
                getAriaLabel?.(index) ??
                (thumbValues.length === 1
                  ? ariaLabel ?? 'Value'
                  : `Value ${index + 1}`)
              }
              className="block size-6 rounded-full border border-primary/50 bg-background shadow transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
            />
          ))}
        </SliderPrimitive.Track>
      </SliderPrimitive.Control>
    </SliderPrimitive.Root>
  )
}

export { Slider }
